import { useEffect, useRef, useState } from 'react';
import type {
  Contractor,
  EstimateOption,
  EstimateWithScopes,
  PreInvoiceEstimate,
  ProjectEstimateView,
} from '@bpd/shared';
import { formatCurrency, computeEstimateRemaining } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';
import { CurrencyInput } from '../CurrencyInput.tsx';
import { loadTaskOptions, taskLabel, type TaskOption } from './taskOptions.ts';
import { ContractorPicker } from './ContractorPicker.tsx';
import type { InvoicePrefill, InvoiceActivity } from './ProjectInvoicesSection.tsx';

const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024; // 15MB — generous for a scanned/photographed quote
const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

function optionsTotal(options: EstimateOption[]): { accepted: number; total: number } {
  return options.reduce(
    (acc, o) => ({
      accepted: acc.accepted + (o.accepted ? o.price : 0),
      total: acc.total + o.price,
    }),
    { accepted: 0, total: 0 },
  );
}

interface AddOptionFormProps {
  estimateId: string;
  projectId: string;
  /** This project's shared task scope for the estimate (see EstimateOption's doc comment) — every
   * additional line added here must reuse it, so it's fixed rather than user-editable. */
  taskItemId?: string;
  onAdded: (option: EstimateOption) => void;
  onCancel: () => void;
}

function AddOptionForm({ estimateId, projectId, taskItemId, onAdded, onCancel }: AddOptionFormProps) {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const p = Number(price);
    if (!label.trim() || !Number.isFinite(p) || p < 0) {
      setError('Label and a non-negative price are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await projectsApi.createEstimateOption({
        estimateId,
        projectId,
        ...(taskItemId ? { taskItemId } : {}),
        label: label.trim(),
        ...(description ? { description } : {}),
        price: p,
      });
      onAdded(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="mt-2 space-y-2 rounded-md border border-gray-200 p-3">
      <input
        type="text"
        placeholder="Option label *"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
      />
      <div className="grid grid-cols-3 gap-2">
        <input
          type="text"
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="col-span-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
        <CurrencyInput value={price} onChange={setPrice} placeholder="Price *" />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Add option'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </form>
  );
}

interface EditOptionFormProps {
  option: EstimateOption;
  estimateId: string;
  onSaved: (option: EstimateOption) => void;
  onCancel: () => void;
}

function EditOptionForm({ option, estimateId, onSaved, onCancel }: EditOptionFormProps) {
  const [label, setLabel] = useState(option.label);
  const [description, setDescription] = useState(option.description ?? '');
  const [price, setPrice] = useState(String(option.price));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    const p = Number(price);
    if (!label.trim() || !Number.isFinite(p) || p < 0) {
      setError('Label and a non-negative price are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await projectsApi.updateEstimateOption(option.id, estimateId, {
        label: label.trim(),
        description,
        price: p,
      });
      onSaved(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border border-phoenix-blue-200 bg-phoenix-blue-50/40 p-3">
      <input
        type="text"
        placeholder="Option label *"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
      />
      <div className="grid grid-cols-3 gap-2">
        <input
          type="text"
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="col-span-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
        <CurrencyInput value={price} onChange={setPrice} placeholder="Price *" />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </div>
  );
}

interface OptionRowProps {
  option: EstimateOption;
  estimateId: string;
  onChanged: (option: EstimateOption) => void;
  onDeleted: (id: string) => void;
}

function OptionRow({ option, estimateId, onChanged, onDeleted }: OptionRowProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);

  async function toggleAccepted() {
    const updated = await projectsApi.updateEstimateOption(option.id, estimateId, { accepted: !option.accepted });
    onChanged(updated);
  }

  async function handleDelete() {
    await projectsApi.deleteEstimateOption(option.id, estimateId);
    onDeleted(option.id);
  }

  if (editing) {
    return (
      <EditOptionForm
        option={option}
        estimateId={estimateId}
        onSaved={(updated) => {
          onChanged(updated);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="flex items-start gap-3 border-t border-gray-100 py-2 first:border-t-0">
      <input type="checkbox" checked={option.accepted} onChange={() => void toggleAccepted()} className="mt-1" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-gray-900">{option.label}</p>
          <span className="text-sm text-gray-600">{formatCurrency(option.price)}</span>
          {option.accepted && (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              Accepted
            </span>
          )}
        </div>
        {option.description && <p className="mt-0.5 text-sm text-gray-500">{option.description}</p>}
      </div>
      <button type="button" onClick={() => setEditing(true)} className="shrink-0 text-xs text-gray-500 hover:underline">
        Edit
      </button>
      {!confirmDelete ? (
        <button type="button" onClick={() => setConfirmDelete(true)} className="shrink-0 text-xs text-red-600 hover:underline">
          Delete
        </button>
      ) : (
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => void handleDelete()}
            className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700"
          >
            Confirm
          </button>
          <button type="button" onClick={() => setConfirmDelete(false)} className="text-xs text-gray-500 hover:underline">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

interface EditEstimateFormProps {
  estimate: ProjectEstimateView;
  contractors: Contractor[];
  taskOptions: TaskOption[];
  /** This project's own slice of the estimate's options — used to retag the task scope in bulk
   * (see handleSave) without touching any other project's lines. */
  options: EstimateOption[];
  onSaved: (estimate: ProjectEstimateView) => void;
  onOptionsRetagged: (options: EstimateOption[]) => void;
  onCancel: () => void;
  onContractorCreated: (contractor: Contractor) => void;
}

function EditEstimateForm({
  estimate,
  contractors,
  taskOptions,
  options,
  onSaved,
  onOptionsRetagged,
  onCancel,
  onContractorCreated,
}: EditEstimateFormProps) {
  const [title, setTitle] = useState(estimate.title);
  const [contractorId, setContractorId] = useState(estimate.contractorId ?? '');
  const [taskItemId, setTaskItemId] = useState(estimate.taskItemId ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Title/contractor live on the (now possibly shared) estimate header — this affects every
      // project the quote is attached to, not just this one.
      const updatedHeader = await projectsApi.updateEstimate(estimate.id, {
        title: title.trim(),
        contractorId: contractorId || null,
      });
      // Task/subtask is a property of THIS project's options, not the shared header — retag every
      // option this estimate has in this project together, one call per option, so the
      // same-task-per-project constraint stays satisfied afterward.
      if (taskItemId !== (estimate.taskItemId ?? '')) {
        const retagged = await Promise.all(
          options.map((o) =>
            projectsApi.updateEstimateOption(o.id, estimate.id, { taskItemId: taskItemId || null }),
          ),
        );
        onOptionsRetagged(retagged);
      }
      onSaved({
        ...updatedHeader,
        ...(taskItemId ? { taskItemId } : {}),
        sharedWithOtherProjects: estimate.sharedWithOtherProjects,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-phoenix-blue-200 bg-phoenix-blue-50/40 p-3">
      <div>
        <label className="block text-xs font-medium text-gray-500">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-0.5 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          maxLength={200}
        />
        {estimate.sharedWithOtherProjects > 0 && (
          <p className="mt-1 text-xs text-amber-600">
            This quote is also used on {estimate.sharedWithOtherProjects} other project
            {estimate.sharedWithOtherProjects === 1 ? '' : 's'} — title and vendor changes apply
            everywhere.
          </p>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500">Vendor / contractor</label>
        <div className="mt-0.5">
          <ContractorPicker
            contractors={contractors}
            value={contractorId}
            onChange={setContractorId}
            onContractorCreated={onContractorCreated}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500">Task / subtask on this project</label>
        <select
          value={taskItemId}
          onChange={(e) => setTaskItemId(e.target.value)}
          className="mt-0.5 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        >
          <option value="">— Project-level (no specific task) —</option>
          {taskOptions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </div>
  );
}

interface AttachmentSectionProps {
  estimate: ProjectEstimateView;
  onUpdated: (estimate: ProjectEstimateView) => void;
}

/** Upload/view/replace/remove the estimate's single quote file (PDF/JPG/PNG). Upload goes straight
 * from the browser to S3 via a presigned PUT URL — the file never passes through this API — and
 * the resulting key is then persisted onto the Estimate via the ordinary update call. The file
 * lives on the (now possibly shared) Estimate header, so it's the same file wherever this quote is
 * attached — replacing it here replaces it everywhere. */
function AttachmentSection({ estimate, onUpdated }: AttachmentSectionProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function handleFileChosen(file: File | null) {
    if (!file) return;
    setError(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError('File is too large (max 15MB)');
      return;
    }
    const contentType = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : '');
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(contentType)) {
      setError('Only PDF, JPG, or PNG files are supported');
      return;
    }
    setBusy(true);
    try {
      const { uploadUrl, key } = await projectsApi.getEstimateUploadUrl(estimate.id, contentType, file.name);
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!putRes.ok) throw new Error('Upload to storage failed');
      const updated = await projectsApi.updateEstimate(estimate.id, {
        attachmentKey: key,
        attachmentFileName: file.name,
        attachmentContentType: contentType,
      });
      onUpdated({ ...estimate, ...updated });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload');
    } finally {
      setBusy(false);
    }
  }

  async function handleView() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await projectsApi.getEstimateDownloadUrl(estimate.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to open file');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    setBusy(true);
    setError(null);
    try {
      await projectsApi.deleteEstimateAttachment(estimate.id);
      const { attachmentKey: _k, attachmentFileName: _f, attachmentContentType: _c, ...rest } = estimate;
      onUpdated(rest);
      setConfirmRemove(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 border-t border-gray-100 pt-2">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Quote file</p>
      {estimate.attachmentFileName ? (
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <span className="text-sm text-gray-700">{estimate.attachmentFileName}</span>
          <button type="button" onClick={() => void handleView()} disabled={busy} className="text-sm text-phoenix-blue-600 hover:underline">
            View
          </button>
          <label className="cursor-pointer text-sm text-phoenix-blue-600 hover:underline">
            Replace
            <input
              type="file"
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              disabled={busy}
              onChange={(e) => void handleFileChosen(e.target.files?.[0] ?? null)}
            />
          </label>
          {!confirmRemove ? (
            <button type="button" onClick={() => setConfirmRemove(true)} className="text-sm text-red-600 hover:underline">
              Remove
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => void handleRemove()}
                disabled={busy}
                className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700"
              >
                Confirm
              </button>
              <button type="button" onClick={() => setConfirmRemove(false)} className="text-sm text-gray-500 hover:underline">
                Cancel
              </button>
            </>
          )}
        </div>
      ) : (
        <label className="mt-1 inline-block cursor-pointer text-sm text-phoenix-blue-600 hover:underline">
          {busy ? 'Uploading…' : '+ Upload quote (PDF, JPG, or PNG)'}
          <input
            type="file"
            accept={ATTACHMENT_ACCEPT}
            className="hidden"
            disabled={busy}
            onChange={(e) => void handleFileChosen(e.target.files?.[0] ?? null)}
          />
        </label>
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

interface AttachExistingEstimateSectionProps {
  projectId: string;
  contractors: Contractor[];
  taskOptions: TaskOption[];
  onContractorCreated: (contractor: Contractor) => void;
  onAttached: (estimate: ProjectEstimateView, option: EstimateOption) => void;
  onCancel: () => void;
}

/** "Reuse an existing quote" — the actual mechanism for one contractor bid covering several
 * projects (e.g. one North Shores Painting quote for gutters/ceiling/exterior across three
 * projects): pick the contractor, pick one of their existing estimates, then add a priced line
 * scoped to *this* project instead of re-entering the whole quote from scratch. */
function AttachExistingEstimateSection({
  projectId,
  contractors,
  taskOptions,
  onContractorCreated,
  onAttached,
  onCancel,
}: AttachExistingEstimateSectionProps) {
  const [contractorId, setContractorId] = useState('');
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [candidates, setCandidates] = useState<EstimateWithScopes[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [taskItemId, setTaskItemId] = useState('');
  const [label, setLabel] = useState('');
  const [price, setPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!contractorId) {
      setCandidates([]);
      setSelectedId('');
      setLoadState('idle');
      return;
    }
    setLoadState('loading');
    projectsApi
      .listEstimatesByContractor(contractorId)
      .then((data) => {
        setCandidates(data);
        setLoadState('ready');
      })
      .catch(() => setLoadState('error'));
  }, [contractorId]);

  const selected = candidates.find((c) => c.id === selectedId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const p = Number(price);
    if (!selected || !label.trim() || !Number.isFinite(p) || p < 0) {
      setError('Pick a quote, and provide a line label and a non-negative price');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const option = await projectsApi.createEstimateOption({
        estimateId: selected.id,
        projectId,
        ...(taskItemId ? { taskItemId } : {}),
        label: label.trim(),
        price: p,
      });
      onAttached(
        {
          ...selected,
          ...(taskItemId ? { taskItemId } : {}),
          sharedWithOtherProjects: selected.scopes.length,
        },
        option,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to attach');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div>
        <label className="block text-xs font-medium text-gray-500">Contractor who gave the quote</label>
        <div className="mt-0.5">
          <ContractorPicker
            contractors={contractors}
            value={contractorId}
            onChange={(v) => {
              setContractorId(v);
              setSelectedId('');
            }}
            onContractorCreated={onContractorCreated}
          />
        </div>
      </div>
      {loadState === 'loading' && <p className="text-sm text-gray-400">Loading their quotes…</p>}
      {loadState === 'error' && (
        <p className="text-sm text-red-600">Couldn&apos;t load quotes for this contractor.</p>
      )}
      {loadState === 'ready' && candidates.length === 0 && (
        <p className="text-sm text-gray-400">
          No existing quotes from this contractor yet — use &quot;+ New estimate&quot; instead.
        </p>
      )}
      {loadState === 'ready' && candidates.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-500">Which quote</label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="mt-0.5 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">— Select —</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} — {formatCurrency(c.totalPrice)}
                {c.scopes.length > 0 ? ` (already on ${c.scopes.map((s) => s.projectName).join(', ')})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      {selected && (
        <>
          <div>
            <label className="block text-xs font-medium text-gray-500">Task / subtask on this project (optional)</label>
            <select
              value={taskItemId}
              onChange={(e) => setTaskItemId(e.target.value)}
              className="mt-0.5 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            >
              <option value="">— Project-level (no specific task) —</option>
              {taskOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Line label — e.g. Gutters *"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="col-span-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
            <CurrencyInput value={price} onChange={setPrice} placeholder="Price *" />
          </div>
        </>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving || !selected}
          className="rounded-md bg-phoenix-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
        >
          {saving ? 'Attaching…' : 'Attach to this project'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </form>
  );
}

interface ProjectEstimatesSectionProps {
  projectId: string;
  contractors: Contractor[];
  onContractorCreated: (contractor: Contractor) => void;
  /** Reports every active estimate's raw accepted-option total (not netted against drawdown), each
   * tagged with its own id and taskItemId, up to ProjectDetailPage — feeds computeBottomLine, which
   * claims each estimate against its own converted invoice (by sourceEstimateId, regardless of that
   * invoice's own taskItemId) before pooling whatever's left into its scope alongside that scope's
   * Products total. isActive has no exclusivity (any number of estimates can be active at once, see
   * Estimate.isActive), so this is every active estimate, not just one. */
  onEstimatesChange: (estimates: PreInvoiceEstimate[]) => void;
  /** Full estimate views (every estimate linked to this project, active or not, with isActive/
   * contractorId) — unlike onEstimatesChange's reduced budget-only projection, this is what
   * ProjectDetailPage's gate board needs for "Estimate active & linked"/"Contractor selected". */
  onEstimateViewsChange?: (estimates: ProjectEstimateView[]) => void;
  /** Hands a "Convert to Invoice" click up to ProjectDetailPage, which forwards it into
   * ProjectInvoicesSection as a pre-fill for its add-invoice form. */
  onConvertToInvoice: (prefill: InvoicePrefill) => void;
  /** Per-invoice paid totals from ProjectInvoicesSection, used to draw down a converted estimate's
   * contribution and to detect which estimates have already been converted (so "Convert to invoice"
   * doesn't offer to convert the same estimate twice — once converted it's a historical record). */
  invoiceActivity: InvoiceActivity[];
  /** Set when arriving here via a specific quote's "View / edit details" link (e.g. from a
   * contractor's Estimates & Quotes list — see ContractorsPage) rather than ordinary in-page
   * browsing. That estimate is auto-expanded, scrolled into view, and briefly ring-highlighted so
   * the jump reads as landing somewhere specific instead of just dropping onto the page — same
   * pattern as ProjectInvoicesSection's Convert-to-Invoice prefill highlight. */
  highlightEstimateId?: string;
}

export function ProjectEstimatesSection({
  projectId,
  contractors,
  onContractorCreated,
  onEstimatesChange,
  onEstimateViewsChange,
  onConvertToInvoice,
  invoiceActivity,
  highlightEstimateId,
}: ProjectEstimatesSectionProps) {
  const [estimates, setEstimates] = useState<ProjectEstimateView[]>([]);
  const [optionsByEstimate, setOptionsByEstimate] = useState<Record<string, EstimateOption[]>>({});
  const [taskOptions, setTaskOptions] = useState<TaskOption[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const estimateRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [justHighlighted, setJustHighlighted] = useState(false);
  const [showAddEstimate, setShowAddEstimate] = useState(false);
  const [showAttachExisting, setShowAttachExisting] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContractorId, setNewContractorId] = useState('');
  const [newTaskItemId, setNewTaskItemId] = useState('');
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [newOptionPrice, setNewOptionPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingOptionFor, setAddingOptionFor] = useState<string | null>(null);
  const [editingEstimateId, setEditingEstimateId] = useState<string | null>(null);
  const [confirmDeleteEstimateId, setConfirmDeleteEstimateId] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([projectsApi.listEstimates(projectId), loadTaskOptions(projectId)]).then(
      async ([data, taskOptionData]) => {
        setEstimates(data);
        setTaskOptions(taskOptionData);
        const active = data.filter((e) => e.isActive);
        const activeOptions = await Promise.all(active.map((e) => projectsApi.listEstimateOptions(e.id)));
        setOptionsByEstimate((prev) => {
          const next = { ...prev };
          active.forEach((e, idx) => {
            // An estimate's options can span several projects now — only this project's are
            // relevant here (its own totals, its own scope-option list).
            next[e.id] = (activeOptions[idx] ?? []).filter((o) => o.projectId === projectId);
          });
          return next;
        });
        setExpanded((prev) => {
          const next = new Set(prev);
          active.forEach((e) => next.add(e.id));
          return next;
        });
        setLoaded(true);
      },
    );
  }, [projectId]);

  useEffect(() => {
    const active: PreInvoiceEstimate[] = estimates
      .filter((e) => e.isActive)
      .map((e) => ({
        estimateId: e.id,
        ...(e.taskItemId ? { taskItemId: e.taskItemId } : {}),
        acceptedTotal: optionsTotal(optionsByEstimate[e.id] ?? []).accepted,
      }));
    onEstimatesChange(active);
  }, [estimates, optionsByEstimate, onEstimatesChange]);

  useEffect(() => {
    onEstimateViewsChange?.(estimates);
  }, [estimates, onEstimateViewsChange]);

  const highlightedOnceRef = useRef(false);
  useEffect(() => {
    if (highlightedOnceRef.current) return;
    if (!loaded || !highlightEstimateId) return;
    if (!estimates.some((e) => e.id === highlightEstimateId)) return;
    highlightedOnceRef.current = true;
    setExpanded((prev) => (prev.has(highlightEstimateId) ? prev : new Set(prev).add(highlightEstimateId)));
    if (!(highlightEstimateId in optionsByEstimate)) {
      void projectsApi.listEstimateOptions(highlightEstimateId).then((options) => {
        setOptionsByEstimate((prev) => ({
          ...prev,
          [highlightEstimateId]: options.filter((o) => o.projectId === projectId),
        }));
      });
    }
    estimateRefs.current.get(highlightEstimateId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setJustHighlighted(true);
    const timer = setTimeout(() => setJustHighlighted(false), 1500);
    return () => clearTimeout(timer);
  }, [loaded, highlightEstimateId, estimates, optionsByEstimate, projectId]);

  async function toggleExpand(estimateId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(estimateId)) next.delete(estimateId);
      else next.add(estimateId);
      return next;
    });
    if (!(estimateId in optionsByEstimate)) {
      const options = await projectsApi.listEstimateOptions(estimateId);
      setOptionsByEstimate((prev) => ({
        ...prev,
        [estimateId]: options.filter((o) => o.projectId === projectId),
      }));
    }
  }

  async function handleAddEstimate(e: React.FormEvent) {
    e.preventDefault();
    const p = Number(newOptionPrice);
    if (!newTitle.trim() || !newOptionLabel.trim() || !Number.isFinite(p) || p < 0) {
      setError('Title, a line label, and a non-negative price are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await projectsApi.createEstimate({
        title: newTitle.trim(),
        ...(newContractorId ? { contractorId: newContractorId } : {}),
      });
      const option = await projectsApi.createEstimateOption({
        estimateId: created.id,
        projectId,
        ...(newTaskItemId ? { taskItemId: newTaskItemId } : {}),
        label: newOptionLabel.trim(),
        price: p,
      });
      const view: ProjectEstimateView = {
        ...created,
        ...(newTaskItemId ? { taskItemId: newTaskItemId } : {}),
        sharedWithOtherProjects: 0,
      };
      setEstimates((prev) => [...prev, view]);
      setExpanded((prev) => new Set(prev).add(created.id));
      setOptionsByEstimate((prev) => ({ ...prev, [created.id]: [option] }));
      setNewTitle('');
      setNewContractorId('');
      setNewTaskItemId('');
      setNewOptionLabel('');
      setNewOptionPrice('');
      setShowAddEstimate(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create estimate');
    } finally {
      setSaving(false);
    }
  }

  function handleAttached(estimate: ProjectEstimateView, option: EstimateOption) {
    setEstimates((prev) => (prev.some((e) => e.id === estimate.id) ? prev : [...prev, estimate]));
    setOptionsByEstimate((prev) => ({
      ...prev,
      [estimate.id]: [...(prev[estimate.id] ?? []), option],
    }));
    setExpanded((prev) => new Set(prev).add(estimate.id));
    setShowAttachExisting(false);
  }

  async function handleMarkActive(id: string) {
    await projectsApi.updateEstimate(id, { isActive: true });
    // isActive has no exclusivity — this only ever affects the one estimate clicked, never siblings.
    setEstimates((prev) => prev.map((e) => (e.id === id ? { ...e, isActive: true } : e)));
    if (!(id in optionsByEstimate)) {
      const options = await projectsApi.listEstimateOptions(id);
      setOptionsByEstimate((prev) => ({ ...prev, [id]: options.filter((o) => o.projectId === projectId) }));
    }
  }

  async function handleMarkInactive(id: string) {
    await projectsApi.updateEstimate(id, { isActive: false });
    setEstimates((prev) => prev.map((e) => (e.id === id ? { ...e, isActive: false } : e)));
  }

  async function handleDeleteEstimate(id: string) {
    // Deletes the whole quote everywhere it's attached, not just this project — see the confirm
    // copy below, which warns when sharedWithOtherProjects > 0. Removing just this project's line
    // from a shared quote is the per-option "Delete" in OptionRow instead.
    await projectsApi.deleteEstimate(id);
    setEstimates((prev) => prev.filter((e) => e.id !== id));
    setOptionsByEstimate((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setConfirmDeleteEstimateId(null);
  }

  function handleEstimateUpdated(updated: ProjectEstimateView) {
    setEstimates((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    setEditingEstimateId(null);
  }

  if (!loaded) return null;

  const contractorName = (id: string | undefined) => contractors.find((c) => c.id === id)?.name;

  return (
    <section className="mt-10">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-phoenix-blue-900">Estimates &amp; Proposals</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setShowAttachExisting((v) => !v);
              setShowAddEstimate(false);
            }}
            className="rounded-md border border-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-phoenix-blue-600 hover:bg-phoenix-blue-50"
          >
            {showAttachExisting ? 'Cancel' : 'Attach existing quote'}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAddEstimate((v) => !v);
              setShowAttachExisting(false);
            }}
            className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700"
          >
            {showAddEstimate ? 'Cancel' : '+ New estimate'}
          </button>
        </div>
      </div>

      {showAttachExisting && (
        <AttachExistingEstimateSection
          projectId={projectId}
          contractors={contractors}
          taskOptions={taskOptions}
          onContractorCreated={onContractorCreated}
          onAttached={handleAttached}
          onCancel={() => setShowAttachExisting(false)}
        />
      )}

      {showAddEstimate && (
        <form
          onSubmit={(e) => void handleAddEstimate(e)}
          className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <input
            type="text"
            placeholder="Estimate title — e.g. Gutter replacement bid *"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            maxLength={200}
            required
          />
          <div>
            <label className="block text-xs font-medium text-gray-500">Vendor / contractor (optional)</label>
            <div className="mt-0.5">
              <ContractorPicker
                contractors={contractors}
                value={newContractorId}
                onChange={setNewContractorId}
                onContractorCreated={onContractorCreated}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500">Task / subtask (optional)</label>
            <select
              value={newTaskItemId}
              onChange={(e) => setNewTaskItemId(e.target.value)}
              className="mt-0.5 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            >
              <option value="">— Project-level (no specific task) —</option>
              {taskOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Line label — e.g. Gutters *"
              value={newOptionLabel}
              onChange={(e) => setNewOptionLabel(e.target.value)}
              className="col-span-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
            <CurrencyInput value={newOptionPrice} onChange={setNewOptionPrice} placeholder="Price *" />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-phoenix-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </form>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      {estimates.length === 0 ? (
        <p className="mt-3 text-sm text-gray-400">No estimates yet.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {estimates.map((estimate) => {
            const options = optionsByEstimate[estimate.id] ?? [];
            const { accepted, total } = optionsTotal(options);
            const isExpanded = expanded.has(estimate.id);
            const isEditing = editingEstimateId === estimate.id;
            // Once THIS estimate has been converted via the button below, it's a historical record —
            // don't offer to convert it again. Separately, "remaining" draws down against ANY
            // invoice paid against the same task (not just ones from this exact conversion — see
            // computeEstimateRemaining), since money collected for a task offsets what's still
            // estimated for it regardless of how that invoice was created.
            const converted = invoiceActivity.some((a) => a.sourceEstimateId === estimate.id);
            const remaining = computeEstimateRemaining(accepted, estimate, invoiceActivity);
            const isHighlighted = justHighlighted && highlightEstimateId === estimate.id;
            return (
              <div
                key={estimate.id}
                ref={(el) => {
                  if (el) estimateRefs.current.set(estimate.id, el);
                  else estimateRefs.current.delete(estimate.id);
                }}
                className={`scroll-mt-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm ring-2 transition-shadow duration-500 ${
                  isHighlighted ? 'ring-phoenix-blue-400' : 'ring-transparent'
                }`}
              >
                {isEditing ? (
                  <EditEstimateForm
                    estimate={estimate}
                    contractors={contractors}
                    taskOptions={taskOptions}
                    options={options}
                    onSaved={handleEstimateUpdated}
                    onOptionsRetagged={(retagged) =>
                      setOptionsByEstimate((prev) => ({ ...prev, [estimate.id]: retagged }))
                    }
                    onCancel={() => setEditingEstimateId(null)}
                    onContractorCreated={onContractorCreated}
                  />
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => void toggleExpand(estimate.id)}
                      className="flex flex-wrap items-center gap-2 text-left"
                    >
                      <span className="text-xs text-phoenix-blue-500">{isExpanded ? '▼' : '▶'}</span>
                      <span className="font-semibold text-gray-900">{estimate.title}</span>
                      {estimate.isActive && (
                        <span className="rounded-full bg-phoenix-blue-100 px-2.5 py-0.5 text-xs font-medium text-phoenix-blue-700">
                          Active
                        </span>
                      )}
                      {contractorName(estimate.contractorId) && (
                        <span className="text-xs text-gray-500">— {contractorName(estimate.contractorId)}</span>
                      )}
                      {estimate.taskItemId && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                          {taskLabel(taskOptions, estimate.taskItemId)}
                        </span>
                      )}
                      {estimate.sharedWithOtherProjects > 0 && (
                        <span
                          className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                          title="This same quote also has line items on other projects"
                        >
                          Also used on {estimate.sharedWithOtherProjects} other project
                          {estimate.sharedWithOtherProjects === 1 ? '' : 's'}
                        </span>
                      )}
                    </button>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>
                        {formatCurrency(accepted)} accepted of {formatCurrency(total)}
                        {converted && <> — {formatCurrency(remaining)} still outstanding</>}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditingEstimateId(estimate.id)}
                        className="text-gray-500 hover:underline"
                      >
                        Edit
                      </button>
                      {estimate.isActive ? (
                        <button
                          type="button"
                          onClick={() => void handleMarkInactive(estimate.id)}
                          className="text-gray-500 hover:underline"
                        >
                          Mark inactive
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void handleMarkActive(estimate.id)}
                          className="text-phoenix-blue-600 hover:underline"
                        >
                          Mark active
                        </button>
                      )}
                      {converted ? (
                        <span
                          className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500"
                          title="An invoice has already been converted from this estimate — it's a historical record now, not re-convertible."
                        >
                          Converted to invoice
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            onConvertToInvoice({
                              title: estimate.title,
                              billedAmount: accepted,
                              ...(estimate.taskItemId ? { taskItemId: estimate.taskItemId } : {}),
                              sourceEstimateId: estimate.id,
                            })
                          }
                          className="text-phoenix-blue-600 hover:underline"
                        >
                          Convert to invoice
                        </button>
                      )}
                      {confirmDeleteEstimateId !== estimate.id ? (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteEstimateId(estimate.id)}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      ) : (
                        <>
                          {estimate.sharedWithOtherProjects > 0 && (
                            <span className="text-xs text-amber-700">
                              Deletes from {estimate.sharedWithOtherProjects + 1} projects total —
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => void handleDeleteEstimate(estimate.id)}
                            className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteEstimateId(null)}
                            className="text-gray-500 hover:underline"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {isExpanded && !isEditing && (
                  <div className="mt-3 border-t border-gray-100 pt-2">
                    {options.length === 0 && <p className="py-1 text-sm text-gray-400">No scope options yet.</p>}
                    {options.map((option) => (
                      <OptionRow
                        key={option.id}
                        option={option}
                        estimateId={estimate.id}
                        onChanged={(updated) =>
                          setOptionsByEstimate((prev) => ({
                            ...prev,
                            [estimate.id]: (prev[estimate.id] ?? []).map((o) => (o.id === updated.id ? updated : o)),
                          }))
                        }
                        onDeleted={(id) =>
                          setOptionsByEstimate((prev) => ({
                            ...prev,
                            [estimate.id]: (prev[estimate.id] ?? []).filter((o) => o.id !== id),
                          }))
                        }
                      />
                    ))}

                    {addingOptionFor === estimate.id ? (
                      <AddOptionForm
                        estimateId={estimate.id}
                        projectId={projectId}
                        {...(estimate.taskItemId ? { taskItemId: estimate.taskItemId } : {})}
                        onAdded={(option) => {
                          setOptionsByEstimate((prev) => ({
                            ...prev,
                            [estimate.id]: [...(prev[estimate.id] ?? []), option],
                          }));
                          setAddingOptionFor(null);
                        }}
                        onCancel={() => setAddingOptionFor(null)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAddingOptionFor(estimate.id)}
                        className="mt-2 text-sm text-phoenix-blue-600 hover:underline"
                      >
                        + Add scope option
                      </button>
                    )}

                    <AttachmentSection estimate={estimate} onUpdated={handleEstimateUpdated} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
